import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Check, KeyRound, CheckCircle2 } from "lucide-react";
import { PageHero } from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { SectionHeading } from "../components/common/SectionHeading";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { IMAGES } from "../lib/data";

const BENEFITS = [
    { title: "Retail-ready", copy: "Sealed, shelf-ready packaging that looks as good as it tastes." },
    { title: "Multiple flavors", copy: "Classics to mystery spins — a shelf people stop for." },
    { title: "Easy reordering", copy: "Case quantities and quick reorders. No phone tag." },
    { title: "Wholesale pricing", copy: "Protected retailer pricing once you're approved." },
    { title: "Local support", copy: "A real NYC team that answers and delivers." },
];

const BIZ_TYPES = ["Deli", "Grocery Store", "Convenience Store", "Specialty Store", "Cafe", "Event Venue", "Gift Shop", "Other"];

const inputCls = "w-full rounded-2xl bg-zinc-900 border border-zinc-800 px-5 py-4 text-sm font-medium text-white outline-none focus:ring-2 focus:ring-pink placeholder:text-zinc-500";
const labelCls = "block text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-400 mb-2";

const INITIAL = {
    business_name: "", contact_name: "", email: "", phone: "", business_address: "",
    business_type: BIZ_TYPES[0], locations: "", website: "", interest: "Both", volume: "", message: "",
};

export const WholesalePage = () => {
    usePageMeta("Wholesale — Stock Showtime In Your Store | Showtime Carnival Co.",
        "Retail-ready cotton candy and popcorn for NYC delis, grocery stores, convenience stores and cafes. Apply to become a Showtime retailer.");
    const [form, setForm] = useState(INITIAL);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/wholesale-inquiries", form);
            setDone(true);
            window.scrollTo(0, 0);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageHero
                kicker="Wholesale"
                title="Put Showtime On Your Shelves."
                copy="Retail-ready cotton candy and popcorn for delis, grocery stores, convenience stores, specialty shops, cafes, venues and gift shops."
                testid="wholesale-hero"
            />
            <section className="bg-white py-20 md:py-28">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10">
                    <SectionHeading chapter="01" kicker="Why Showtime" title="Built To Move Off Shelves." testid="wholesale-benefits-heading" />
                    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="wholesale-benefits">
                        {BENEFITS.map((b, i) => (
                            <Reveal key={b.title} delay={i * 0.06}>
                                <div className="rounded-3xl border border-zinc-200 bg-paper p-8 h-full">
                                    <Check size={20} className="text-pink-dark mb-4" />
                                    <h3 className="font-oswald uppercase text-xl">{b.title}</h3>
                                    <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{b.copy}</p>
                                </div>
                            </Reveal>
                        ))}
                        <Reveal delay={0.3}>
                            <div className="rounded-3xl overflow-hidden h-full min-h-[220px]">
                                <img src={IMAGES.packaged} alt="Packaged Showtime treats on a retail display" loading="lazy" className="h-full w-full object-cover" />
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>
            <section className="bg-ink tent-stripes text-white py-20 md:py-28">
                <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid gap-14 lg:grid-cols-2 items-start">
                    <div>
                        <SectionHeading
                            chapter="02"
                            kicker="Get Approved"
                            title="Become A Retailer."
                            copy="Apply in two minutes. We review every shop by hand, then unlock wholesale pricing, case quantities and rapid reorder."
                            dark
                            testid="wholesale-apply-heading"
                        />
                        <Reveal>
                            <Link
                                to="/retailer/login"
                                data-testid="wholesale-retailer-login"
                                className="inline-flex items-center gap-2 rounded-full border-2 border-pink px-7 py-3.5 font-oswald uppercase tracking-widest text-sm text-pink hover:bg-pink hover:text-ink transition-colors"
                            >
                                <KeyRound size={15} /> Retailer Login
                            </Link>
                        </Reveal>
                    </div>
                    <Reveal delay={0.1}>
                        {done ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="rounded-3xl border border-pink bg-ink/60 backdrop-blur p-10 text-center"
                                data-testid="wholesale-confirmation"
                            >
                                <CheckCircle2 size={56} className="mx-auto text-pink mb-6" />
                                <h2 className="font-oswald uppercase text-4xl text-pink">Welcome To The Show.</h2>
                                <p className="mt-4 text-zinc-300 text-sm leading-relaxed">
                                    Thanks for your interest in stocking Showtime. We'll review your information and contact you with wholesale details.
                                </p>
                            </motion.div>
                        ) : (
                            <form onSubmit={submit} data-testid="wholesale-application-form" className="rounded-3xl border border-zinc-800 bg-ink/60 backdrop-blur p-7 md:p-10">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="ws-biz" className={labelCls}>Business Name *</label>
                                        <input id="ws-biz" required value={form.business_name} onChange={set("business_name")} placeholder="Bed-Stuy Fine Deli" className={inputCls} data-testid="wholesale-business-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-contact" className={labelCls}>Contact Name *</label>
                                        <input id="ws-contact" required value={form.contact_name} onChange={set("contact_name")} placeholder="Sam Ortiz" className={inputCls} data-testid="wholesale-contact-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-email" className={labelCls}>Email *</label>
                                        <input id="ws-email" type="email" required value={form.email} onChange={set("email")} placeholder="you@store.com" className={inputCls} data-testid="wholesale-email-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-phone" className={labelCls}>Phone *</label>
                                        <input id="ws-phone" type="tel" required value={form.phone} onChange={set("phone")} placeholder="(718) 555-0134" className={inputCls} data-testid="wholesale-phone-input" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="ws-address" className={labelCls}>Business Address *</label>
                                        <input id="ws-address" required value={form.business_address} onChange={set("business_address")} placeholder="Street, city, state, zip" className={inputCls} data-testid="wholesale-address-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-type" className={labelCls}>Business Type *</label>
                                        <select id="ws-type" value={form.business_type} onChange={set("business_type")} className={inputCls} data-testid="wholesale-type-select">
                                            {BIZ_TYPES.map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label htmlFor="ws-locations" className={labelCls}>Number of Locations</label>
                                        <input id="ws-locations" value={form.locations} onChange={set("locations")} placeholder="1" className={inputCls} data-testid="wholesale-locations-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-website" className={labelCls}>Website / Social</label>
                                        <input id="ws-website" value={form.website} onChange={set("website")} placeholder="@yourstore or URL" className={inputCls} data-testid="wholesale-website-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ws-interest" className={labelCls}>Interested In *</label>
                                        <select id="ws-interest" value={form.interest} onChange={set("interest")} className={inputCls} data-testid="wholesale-interest-select">
                                            {["Cotton Candy", "Popcorn", "Both"].map((t) => <option key={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="ws-volume" className={labelCls}>Estimated Order Volume</label>
                                        <input id="ws-volume" value={form.volume} onChange={set("volume")} placeholder="e.g. 2 cases a week" className={inputCls} data-testid="wholesale-volume-input" />
                                    </div>
                                    <div className="sm:col-span-2">
                                        <label htmlFor="ws-msg" className={labelCls}>Notes</label>
                                        <textarea id="ws-msg" rows={3} value={form.message} onChange={set("message")} placeholder="Shelf space near the register, two locations..." className={inputCls} data-testid="wholesale-message-input" />
                                    </div>
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="wholesale-submit-button"
                                    className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-pink py-4 font-oswald uppercase tracking-widest text-ink hover:bg-white transition-colors duration-300 active:scale-95 disabled:opacity-50"
                                >
                                    {loading ? "Sending..." : "Apply Now"} <ArrowRight size={16} />
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </section>
        </>
    );
};
